import React from 'react';
import { Box, Chip, Divider, Stack, Typography } from '@mui/material';
import {
  AutoAwesome as DraftIcon,
  ThumbUp as ApprovedIcon,
  Send as PostedIcon,
  CheckCircle as PaidIcon,
  Schedule as PartialIcon,
  Undo as ReversedIcon,
  Block as VoidIcon,
} from '@mui/icons-material';

// ---------------------------------------------------------------------------
// Shared Accounts Receivable UI kit - same visual vocabulary as
// cashbank/CashShared.js and Journals.js so the ledger screens read as one
// product: outlined icon status chips, bordered stat strips, plus the
// envelope/format helpers every AR tab repeats.
// ---------------------------------------------------------------------------

export const getErrorMessage = (error, fallback = 'Operation failed') =>
  error?.response?.data?.message || fallback;

export const unwrap = (response) => response?.data?.data ?? response?.data ?? {};

export const unwrapList = (response) => {
  const payload = unwrap(response);
  return Array.isArray(payload) ? payload : payload.items ?? [];
};

export const fmtDate = (d) => {
  if (!d) return '-';
  const dt = new Date(d);
  return Number.isNaN(dt.getTime())
    ? String(d)
    : dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

// DRAFT/APPROVED/POSTED/VOID mirror the journal/cash-voucher lifecycle;
// PARTIALLY_PAID/PAID/REVERSED are the invoice-specific states the ledger
// reports back (see AccountingDashboard.js's statusColor, which already
// handles these from the same ledger documents).
const STATUS_CFG = {
  DRAFT: { label: 'Draft', color: 'default', Icon: DraftIcon },
  APPROVED: { label: 'Approved', color: 'success', Icon: ApprovedIcon },
  POSTED: { label: 'Posted', color: 'info', Icon: PostedIcon },
  PARTIALLY_PAID: { label: 'Partially Paid', color: 'warning', Icon: PartialIcon },
  PAID: { label: 'Paid', color: 'success', Icon: PaidIcon },
  REVERSED: { label: 'Reversed', color: 'warning', Icon: ReversedIcon },
  VOID: { label: 'Void', color: 'error', Icon: VoidIcon },
};

export const ArStatusChip = ({ status }) => {
  const st = String(status || 'DRAFT').toUpperCase();
  const cfg = STATUS_CFG[st] || { label: status || 'Draft', color: 'default', Icon: DraftIcon };
  const { Icon } = cfg;
  return <Chip icon={<Icon fontSize="small" />} label={cfg.label} color={cfg.color} size="small" variant="outlined" />;
};

export const StatTile = ({ label, value, color }) => (
  <Box sx={{ flex: '1 1 140px', textAlign: 'center', py: 1.5, px: 1 }}>
    <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
    <Typography variant="h6" fontWeight={700} color={color} sx={{ fontVariantNumeric: 'tabular-nums' }}>
      {value}
    </Typography>
  </Box>
);

export const StatStrip = ({ tiles = [] }) => (
  <Stack
    direction="row"
    flexWrap="wrap"
    divider={<Divider orientation="vertical" flexItem />}
    sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 2 }}
  >
    {tiles.map((t) => (
      <StatTile key={t.label} {...t} />
    ))}
  </Stack>
);
